import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import { useDispatch } from 'react-redux';
import { setCityBoundary } from '../../store/slices/uiSlice';
import './CityStateBoundaryLayer.css';
import L from 'leaflet';

const CityStateBoundaryLayer = ({ cityName, stateName, countryCode = 'in', cityZoomLevel = 5, defaultCenter, defaultZoom, onLoadingChange, disableDefaultRecenter = false }) => {
  const map = useMap();
  const dispatch = useDispatch();
  const layerRef = useRef(null);
  const maskLayerRef = useRef(null);

  useEffect(() => {
    const query = cityName || stateName;
    if (!query) {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
      if (maskLayerRef.current) {
        map.removeLayer(maskLayerRef.current);
        maskLayerRef.current = null;
      }
      // Clear boundary in Redux when city/state is cleared
      dispatch(setCityBoundary(null));
      if (onLoadingChange) onLoadingChange(false);
      // When city/state is cleared, optionally recenter to the provided default
      if (!disableDefaultRecenter && defaultCenter && Array.isArray(defaultCenter) && typeof defaultZoom === 'number') {
        try {
          map.flyTo(defaultCenter, defaultZoom, { animate: true, duration: 1.0 });
        } catch (e) {
          // ignore
        }
      }
      return;
    }

    let aborted = false;

    const fetchBoundary = async () => {
      if (onLoadingChange) onLoadingChange(true);
      try {
        // Build more specific query for better results
        let searchQuery = query;
        const cityLower = cityName ? cityName.toLowerCase() : '';
        const stateLower = stateName ? stateName.toLowerCase() : '';
        
        if (cityName) {
          if (cityLower === 'mumbai') {
            // Try multiple formats for Mumbai - start with the most specific
            searchQuery = 'Mumbai Suburban, Maharashtra, India';
          } else if (cityLower === 'goa' || cityLower === 'goa state') {
            searchQuery = 'Goa, India';
          } else {
            // For other cities, use city name with state if available
            if (stateName) {
              searchQuery = `${cityName}, ${stateName}, India`;
            } else {
              searchQuery = `${cityName}, India`;
            }
          }
        } else if (stateName) {
          searchQuery = `${stateName}, India`;
        }
        
        console.log('🌍 [CityStateBoundaryLayer] Fetching boundary for:', searchQuery);
        
        // For Mumbai, try multiple query formats to get the entire metropolitan region
        let mumbaiQueries = [];
        if (cityLower === 'mumbai') {
          mumbaiQueries = [
            'Mumbai Metropolitan Region, Maharashtra, India',
            'Greater Mumbai, Maharashtra, India',
            'Mumbai Suburban District, Maharashtra, India',
            'Mumbai City District, Maharashtra, India',
            'Mumbai, Maharashtra, India'
          ];
        }
        
        let data = null;
        let usedQuery = searchQuery;
        
        // If Mumbai, try multiple queries
        if (mumbaiQueries.length > 0) {
          for (const mumbaiQuery of mumbaiQueries) {
            console.log(`🔄 [CityStateBoundaryLayer] Trying Mumbai query: ${mumbaiQuery}`);
            const url = `https://nominatim.openstreetmap.org/search?format=json&polygon_geojson=1&limit=5&countrycodes=${encodeURIComponent(countryCode)}&q=${encodeURIComponent(mumbaiQuery)}`;
        const res = await fetch(url, {
          headers: {
            Accept: 'application/json',
            'User-Agent': 'AtlasApp/1.0 (support@atlas.local)'
          }
        });
            if (!res.ok) {
              console.warn(`⚠️ [CityStateBoundaryLayer] Query failed: ${mumbaiQuery}`, res.status);
              continue;
            }
            const responseData = await res.json();
        if (aborted) return;
            
            // Find the first result with geojson
            const resultWithGeoJson = Array.isArray(responseData) 
              ? responseData.find(r => r.geojson) 
              : null;
            
            if (resultWithGeoJson) {
              console.log(`✅ [CityStateBoundaryLayer] Found boundary with query: ${mumbaiQuery}`, resultWithGeoJson.display_name);
              data = [resultWithGeoJson];
              usedQuery = mumbaiQuery;
              break;
            } else {
              console.log(`⚠️ [CityStateBoundaryLayer] No GeoJSON in response for: ${mumbaiQuery}`);
            }
          }
        } else {
          // For non-Mumbai cities, use the normal query
          const url = `https://nominatim.openstreetmap.org/search?format=json&polygon_geojson=1&limit=1&countrycodes=${encodeURIComponent(countryCode)}&q=${encodeURIComponent(searchQuery)}`;
          const res = await fetch(url, {
            headers: {
              Accept: 'application/json',
              'User-Agent': 'AtlasApp/1.0 (support@atlas.local)'
            }
          });
          if (!res.ok) {
            console.error('❌ [CityStateBoundaryLayer] API request failed:', res.status, res.statusText);
            if (onLoadingChange) onLoadingChange(false);
            return;
          }
          data = await res.json();
          if (aborted) return;
        }
        
        if (!data) {
          console.error('❌ [CityStateBoundaryLayer] No data received from API');
          if (onLoadingChange) onLoadingChange(false);
          return;
        }
        
        console.log('📍 [CityStateBoundaryLayer] API response:', data.length, 'results');

        // Clear existing layer
        if (layerRef.current) {
          map.removeLayer(layerRef.current);
          layerRef.current = null;
        }

        if (Array.isArray(data) && data.length > 0 && data[0].geojson) {
          const geoJson = data[0].geojson;
          console.log('✅ [CityStateBoundaryLayer] Boundary found:', data[0].display_name, 'GeoJSON type:', geoJson.type);
          
          // Store boundary in Redux for device filtering
          dispatch(setCityBoundary(geoJson));
          
          const style = { color: '#ef4444', weight: 2, fillOpacity: 0.08 };
          layerRef.current = L.geoJSON(geoJson, { style });
          layerRef.current.addTo(map);
          console.log('✅ [CityStateBoundaryLayer] Boundary layer added to map');
          // Build dimming mask outside the selected area
          if (maskLayerRef.current) {
            map.removeLayer(maskLayerRef.current);
            maskLayerRef.current = null;
          }
          const worldRing = [
            [90, -180],
            [90, 180],
            [-90, 180],
            [-90, -180]
          ];
          const holes = [];
          layerRef.current.eachLayer((featureLayer) => {
            if (featureLayer && typeof featureLayer.getLatLngs === 'function') {
              const latLngs = featureLayer.getLatLngs();
              const normalizeRings = (rings) => {
                if (!Array.isArray(rings)) return [];
                if (rings.length > 0 && Array.isArray(rings[0]) && Array.isArray(rings[0][0])) {
                  return rings.flat();
                }
                return rings;
              };
              const rings = normalizeRings(latLngs);
              rings.forEach(r => {
                if (Array.isArray(r) && r.length > 2) {
                  holes.push(r.map(ll => [ll.lat, ll.lng]));
                }
              });
            }
          });
          if (holes.length > 0) {
            maskLayerRef.current = L.polygon([worldRing, ...holes], {
              color: '#000000',
              weight: 0,
              fillColor: '#000000',
              fillOpacity: 0.35,
              interactive: false
            });
            maskLayerRef.current.addTo(map);
          }
          const bounds = layerRef.current.getBounds();
          if (bounds && bounds.isValid()) {
            // For Mumbai, add extra padding to ensure entire region is visible
            const padding = cityLower === 'mumbai' ? [80, 80] : [24, 24];
            console.log('🗺️ [CityStateBoundaryLayer] Fitting bounds with padding:', padding);
            map.fitBounds(bounds, { padding, maxZoom: cityLower === 'mumbai' ? 12 : undefined });
          } else if (cityName) {
            // Fallback: use coordinates if bounds are invalid
            const lat = parseFloat(data[0].lat);
            const lon = parseFloat(data[0].lon);
            if (!Number.isNaN(lat) && !Number.isNaN(lon)) {
              const zoom = cityLower === 'mumbai' ? 11 : cityZoomLevel;
              map.flyTo([lat, lon], zoom, { animate: true, duration: 1.2 });
            }
          }
        } else {
          console.warn('⚠️ [CityStateBoundaryLayer] No boundary GeoJSON found in response');
          if (data && data.length > 0) {
            console.log('📋 [CityStateBoundaryLayer] First result:', {
              display_name: data[0].display_name,
              type: data[0].type,
              has_geojson: !!data[0].geojson,
              osm_type: data[0].osm_type,
              osm_id: data[0].osm_id
            });
          }
        }
      } catch (e) {
        console.error('❌ [CityStateBoundaryLayer] Error fetching boundary:', e);
      } finally {
        if (!aborted && onLoadingChange) onLoadingChange(false);
      }
    };

    fetchBoundary();

    return () => {
      aborted = true;
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
      if (maskLayerRef.current) {
        map.removeLayer(maskLayerRef.current);
        maskLayerRef.current = null;
      }
    };
  }, [cityName, stateName, map, disableDefaultRecenter, defaultCenter, defaultZoom, onLoadingChange, countryCode, cityZoomLevel, dispatch]);

  return null;
};

export default CityStateBoundaryLayer;


